import { I18n, Translate } from 'common'
import {
  KeyboardEventHandler,
  ReactElement,
  ReactNode,
  CSSProperties,
} from 'react'
import { defaultTheme } from 'src/theme'
import { breakpoints } from 'src/theme/breakpoints'
import styled, { css } from 'styled-components'

interface OrderFormTextInputProps {
  value: string
  label?: string
  placeholder?: string
  onChange: (value: string) => void
  icon?: ReactNode
  style?: CSSProperties
  disabled?: boolean
  width?: string
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>
  error?: boolean
  onValueChange?: (value: string) => void
  required?: boolean
  showError?: boolean
  showMaxSize?: boolean
  showSubLabel?: boolean
  maxSize?: number
  noMargin?: boolean
}

export const OrderFormTextInput = (
  props: OrderFormTextInputProps
): ReactElement => {
  const {
    value,
    onChange,
    placeholder = '',
    label = '',
    icon,
    disabled = false,
    width = '200px',
    required = false,
    showError = false,
    error = false,
    onValueChange,
    showMaxSize = false,
    maxSize,
    showSubLabel = false,
    noMargin = false,
    ...restOfProps
  } = props

  return (
    <Wrapper width={width} noMargin={noMargin} className="text-input-wrapper">
      <Label>
        <I18n name={label} />
      </Label>
      <InputWrapper disabled={disabled} error={error}>
        <Input
          type="text"
          onChange={(e) => onChange(e.target.value)}
          placeholder={Translate(placeholder)}
          disabled={disabled}
          value={value}
          maxLength={maxSize}
          {...restOfProps}
        />
      </InputWrapper>
      {showMaxSize && (
        <SizeText error={value?.length > (maxSize || 0)}>
          {value?.length || 0}/{maxSize}
        </SizeText>
      )}
      {showSubLabel && (
        <SubLabel>
          <I18n name={'forShopUse'} />
        </SubLabel>
      )}
      {required && showError && !disabled && value?.length === 0 && (
        <SubLabel required={required}>
          <I18n name={'required'} />
        </SubLabel>
      )}
    </Wrapper>
  )
}

interface WrapperProps {
  width: string
  noMargin: boolean
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${(p) => p.width};
  flex: 1 1 ${(p) => p.width};
  min-width: 0;
  margin-left: ${(p) => (p.noMargin ? '0' : '20px')};

  @media (max-width: ${breakpoints.tablet}px) {
    max-width: 100%;
    flex: 1 1 100%;
    margin-left: 0;
  }
`
const Label = styled.div`
  font-family: Roboto, serif;
  font-size: 14px;
  line-height: 24px;
  align-self: flex-start;
  margin-left: 5px;
  font-weight: bold;
`

const InputStyle = css`
  display: inline-block;
  border: none;
  padding-left: 16px;
  padding-right: 16px;
  border-radius: 4px;
  height: 48px;
  width: 100%;
  caret-color: ${(p) => p.theme.colors.primary};
`
const Input = styled.input`
  ${InputStyle}
`

interface InputWrapperProps {
  disabled: boolean
  error: boolean
}

const InputWrapper = styled.span<InputWrapperProps>`
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid
    ${(p) => (p.error ? p.theme.colors.red : p.theme.colors.border)};
  border-radius: 4px;
  color: ${(p) => p.theme.colors.text};
  background-color: ${(p) =>
    p.disabled ? p.theme.colors.disabledBackground : '#FFF'};
`

interface TextProps {
  error?: boolean
  required?: boolean
}
const SizeText = styled.h6<TextProps>`
  font-family: Roboto;
  margin-top: 5px;
  margin-bottom: 0px;
  font-size: 10px;
  color: ${(p) => (p.error ? defaultTheme.colors.red : 'gray')};
  align-self: flex-start;
`

const SubLabel = styled.h5<TextProps>`
  font-family: Roboto;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 13px;
  margin-top: 8px;
  color: ${(p) => (p.required ? defaultTheme.colors.red : 'rgb(5, 5, 5)')};
`
